import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { libraryReleaseClient } from './reuseClient';
/** A permissioned released-source comparison. PP local revisions remain owned
 * by the tailoring editor; accepting here updates only the Library adoption. */ export function SourceChangeNotice({ reuseId, onAccepted }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const change = useQuery({
        queryKey: [
            'library-source-change',
            reuseId
        ],
        enabled: !!reuseId,
        retry: false,
        queryFn: async ()=>{
            try {
                return (await libraryReleaseClient.compareLibrarySourceChange({
                    reuseId
                })).change ?? null;
            } catch (e) {
                if (e instanceof ConnectError && e.code === Code.NotFound) return null;
                throw e;
            }
        }
    });
    const value = change.data;
    if (change.isLoading && reuseId) return /*#__PURE__*/ _jsx("output", {
        children: "Checking source changes…"
    });
    if (change.isError) return /*#__PURE__*/ _jsxs("p", {
        role: "alert",
        children: [
            "Source change review could not be loaded.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                onClick: ()=>void change.refetch(),
                children: "Retry"
            })
        ]
    });
    if (!value) return null;
    if (value.decision) return /*#__PURE__*/ _jsxs("p", {
        "data-testid": Testids.libraryChangeReview.decision,
        children: [
            value.decision === 'keep_mine' ? 'Kept local wording' : 'Accepted source update',
            " — released v",
            value.newVersion
        ]
    });
    const decide = async (decision)=>{
        setBusy(true);
        setError('');
        try {
            const result = await libraryReleaseClient.reviewLibrarySourceChange({
                reuseId,
                releaseId: value.releaseId,
                decision,
                expectedRevision: value.adoptedRevision
            });
            if (decision === 'accept' && result.reuse) onAccepted?.(result.reuse.content);
            await Promise.all([
                qc.invalidateQueries({
                    queryKey: [
                        'library-reuses'
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'library-source-change',
                        reuseId
                    ]
                })
            ]);
        } catch  {
            setError('The source or your accepted edits changed, or access is unavailable. Compare again before deciding.');
            setOpen(false);
            await change.refetch();
        } finally{
            setBusy(false);
        }
    };
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2 rounded border border-warning p-3",
        "data-testid": Testids.libraryChangeReview.notice,
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "font-medium",
                children: [
                    "Source changed — released v",
                    value.newVersion,
                    ". Review before relying on this reference."
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Your accepted wording stays in place until you choose. Pursuit tailoring and submitted snapshots stay unchanged."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                "data-testid": Testids.libraryChangeReview.compare,
                onClick: ()=>setOpen(!open),
                children: open ? 'Hide comparison' : 'Compare'
            }),
            open && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "grid gap-3 md:grid-cols-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsxs("h4", {
                                        children: [
                                            "Previous source — v",
                                            value.sourceVersion
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "whitespace-pre-wrap",
                                        "data-testid": Testids.libraryChangeReview.oldContent,
                                        children: value.oldContent
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsxs("h4", {
                                        children: [
                                            "New source — v",
                                            value.newVersion
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "whitespace-pre-wrap",
                                        "data-testid": Testids.libraryChangeReview.newContent,
                                        children: value.newContent
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        "data-testid": Testids.libraryChangeReview.reason,
                        children: [
                            "Correction reason:",
                            ' ',
                            value.correctionReason || 'No correction reason recorded for this release.'
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: busy,
                                "data-testid": Testids.libraryChangeReview.accept,
                                onClick: ()=>void decide('accept'),
                                children: busy ? 'Saving…' : 'Accept source wording'
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                disabled: busy,
                                "data-testid": Testids.libraryChangeReview.keepMine,
                                onClick: ()=>void decide('keep_mine'),
                                children: "Keep mine"
                            })
                        ]
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: error
            })
        ]
    });
}
